import { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getLabel } from '../../data/labels';
import { parsePrintAgg } from '../../data/data';
import { fetchBetsForTournaments } from '../../data/fetchBetsForTournaments';
import { compactBets } from '../../data/parseBetsData';
import { getBetDescription, getConfiguration } from '../../data/utils';
import { ReactComponent as PrintIcon } from '../../icons/print.svg';
import { PrematchDataContext } from '../PrematchDataFetcher';
import { PrintIframeContent as CompactPrint } from '../print-odds-2/PrintIframeContent';
import { PrintIframeContent as DefaultPrint } from '../print-odds/PrintIframeContent';
import { getMapFromBets } from '../print-odds/PrintOdds';

const settings = {
    align: 'vertical',
    fontSize: 'medium',
    sortType: 'tournament',
    range: 'ALL',
    dateFrom: null,
    dateTo: null,
    logoOnAllPages: true
};

function getBetsToPrint(prematchData, tournaments, aggId) {
    const conf = getConfiguration();
    const sport = prematchData.sportById[tournaments[0].sportRef];
    let bets = [];

    if (conf.pot === 'COMPACT') {
        tournaments.forEach(t => {
            const category = sport.categoryById[t.categoryRef];
            if (category == null) {
                return;
            }

            const tournament = prematchData.tournamentByUniqueId[t.uniqueId];
            if (tournament == null) {
                return;
            }

            t.sportDesc = sport.descrizione;
            t.categoryDesc = sport.categoryById[t.categoryRef].descrizione;
        });
        
        try {
            const printAggs = JSON.parse(conf.poa);
            let printAgg = printAggs[sport.id].pr[0];
            printAgg = parsePrintAgg(printAgg);
            let aggBets = printAgg.scommesse;
            if (printAgg.compattate != null && Object.values(printAgg.compattate).length > 0) {
                aggBets = compactBets(aggBets, printAgg, false);
            }
            aggBets.forEach(b => b.sportRef = sport.id);
            bets = aggBets;
        } catch(e) {}
    } else {
        const betsById = new Set();
        tournaments.forEach(t => {
            t.sportDesc = sport.descrizione;
            const category = sport.categoryById[t.categoryRef];
            if (category == null) {
                return;
            }
            t.categoryDesc = sport.categoryById[t.categoryRef].descrizione;
    
            const tournament = prematchData.tournamentByUniqueId[t.uniqueId];
            if (tournament == null) {
                return;
            }
    
            const agg = tournament.aggregate.find(agg => agg.idAggregata === aggId);
            if (agg == null) {
                return;
            }
    
            agg.scommesse.forEach(bet => {
                const betId = `${bet.id}_${bet.handicap}`;
                if (!betsById.has(betId)) {
                    betsById.add(betId);
                    if (!bet.processed) {
                        bet.desc = getBetDescription(bet, true);
                        bet.processed = true;
                    }
                    bet.agId = agg.idAggregata;
                    bet.agRanking = agg.ranking;
                    bet.sportRef = t.sportRef;
                    bet.categoryRef = t.categoryRef;
                    bet.tournamentRef = t.id;
                    bets.push(bet);
                }
            });
    
            if (agg.compattate != null && Object.values(agg.compattate).length > 0) {
                bets = compactBets(bets, agg, false);
            }
        });
    }

    const betsToPrint = Object.values(getMapFromBets(bets)).sort((a, b) => {
        if (a.agRanking === b.agRanking) {
            return a.ranking - b.ranking;
        } else {
            return a.agRanking - b.agRanking;
        }
    });

    return betsToPrint;
}

function Default(props) {
    const iframeRef = useRef(null);
    
    useEffect(() => {
        if (props.groups) {
            setTimeout(() => {
                iframeRef.current.contentWindow.print();
            }, 500);
        }
    }, [props.togglePrint]);
    
    return (
        <iframe title="print-odds-iframe" style={{display: 'none'}} ref={iframeRef}>
            {props.groups && iframeRef.current && ReactDOM.createPortal(
                <DefaultPrint groups={props.groups}
                              groupByBet={props.groupByBet}
                              settings={settings}
                />
                , iframeRef.current.contentWindow.document.body)}
        </iframe>
    );
}

function Compact(props) {
    const iframeRef = useRef(null);

    useEffect(() => {
        if (props.groups) {
            const cw = iframeRef.current.contentWindow;
            if (cw.logoLoaded === true) {
                cw.print();
            }
        }
    }, [props.togglePrint]);
    
    return (
        <iframe title="print-odds-iframe" style={{display: 'none'}} ref={iframeRef}>
            {props.groups && iframeRef.current && ReactDOM.createPortal(
                <CompactPrint groups={props.groups}
                              groupByBet={props.groupByBet}
                />
                , iframeRef.current.contentWindow.document.body)}
        </iframe>
    );
}

export default function PrintOddsButton(props) {
    const conf = getConfiguration();
    const prematchData = useContext(PrematchDataContext);
    const [state, setState] = useState({
        groups: null,
        groupByBet: null,
        update(updt) {
            setState(prev => {
                return {...prev, ...updt};
            });
        },
        setData(groups, groupByBet) {
            this.update({groups, groupByBet});
        },
    });
    const [togglePrint, setTogglePrint] = useState(false);

    function onClick() {
        const tournaments = props.tournaments;

        const betsToPrint = getBetsToPrint(prematchData, tournaments, props.agg);
        if (betsToPrint.length === 0) {
            return;
        }
        
        const d = props.date;
        const date = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
        fetchBetsForTournaments(tournaments, betsToPrint, null, date, date, function(resp) {
            state.setData(resp[0], resp[1]);
            if (resp[0].length > 0) {
                setTogglePrint(!togglePrint);
            }
        });
    }

    return (
        <>
            <div className="sort-button position-relative print-button" onClick={onClick}>
                <PrintIcon className="pointer" />
                <span>{getLabel('general.print')}</span>
            </div>
            {conf.pot === 'COMPACT' ?
                <Compact togglePrint={togglePrint}
                         tournaments={props.tournaments}
                         groups={state.groups}
                         groupByBet={state.groupByBet}
                />
                :
                <Default togglePrint={togglePrint}
                         tournaments={props.tournaments}
                         groups={state.groups}
                         groupByBet={state.groupByBet}
                /> 
            }
        </>
    );
}
